import {useEffect, useMemo, useRef, useState} from 'react'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {resolveRedirect, RedirectResult} from '_utils'

/**
 * A single breadcrumb item as produced by the backend.
 * `url` is a locale-relative path (e.g. `/it-it/donna/how-to-style`).
 */
export interface BreadcrumbItemInput {
    id: string
    htmlValue: string
    url?: string
    navigable?: boolean
}

/** A breadcrumb item enriched with a resolved redirect, if any. */
export interface ResolvedBreadcrumbItem extends BreadcrumbItemInput {
    /**
     * Redirect resolved via getUrlMapping for this item's `url`. `undefined` while not yet
     * resolved or when the item is the current page / not navigable.
     */
    redirect?: RedirectResult
}

/** Removes the leading locale segment and slash: `/it-it/donna/x` → `donna/x`. */
function toUrlSegment(url: string): string {
    return url.replace(/^\/[a-z]{2}-[a-z]{2}(?=\/|$)/i, '').replace(/^\//, '')
}

/** Schedules work when the browser is idle, falling back to a macrotask. */
function scheduleIdle(cb: () => void): () => void {
    if (typeof window === 'undefined') return () => undefined

    const w = window as typeof window & {
        requestIdleCallback?: (cb: () => void, opts?: {timeout: number}) => number
        cancelIdleCallback?: (id: number) => void
    }

    if (typeof w.requestIdleCallback === 'function') {
        const id = w.requestIdleCallback(cb, {timeout: 2000})
        return () => w.cancelIdleCallback?.(id)
    }

    const id = window.setTimeout(cb, 200)
    return () => window.clearTimeout(id)
}

/**
 * @name useBreadcrumbRedirects
 *
 * @description
 * Pre-resolves breadcrumb redirects on the client, without hurting Core Web Vitals.
 *
 * The backend cannot pre-resolve breadcrumb redirects (URLRedirectMgr only resolves the current
 * request URL). So the PWA resolves each ancestor breadcrumb item via `getUrlMapping` and, if the
 * item redirects (301/302), rewrites its destination so the click goes straight to the final page
 * (no intermediate landing + bounce).
 *
 * Performance strategy:
 * - Runs only after mount, inside `requestIdleCallback` (never during the critical load), so it
 *   does not affect LCP/TTFB. The breadcrumb layout is unchanged, so no CLS.
 * - Resolves items sequentially (concurrency 1) from the penultimate item to the first (the last
 *   item is the current page and is skipped), so the closest-to-current ancestors resolve first.
 * - Results are cached per URL for the lifetime of the hook; re-runs are avoided.
 * - Because resolution happens in the background before the click, the click stays instant (good INP).
 *
 * @param items - Breadcrumb items from the backend (last one is the current page).
 * @returns The same items, each enriched with a resolved `redirect` when applicable.
 */
export function useBreadcrumbRedirects(items?: Array<BreadcrumbItemInput>): Array<ResolvedBreadcrumbItem> {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const [redirectsByUrl, setRedirectsByUrl] = useState<Record<string, RedirectResult>>({})

    // Candidate items to resolve: navigable, with a url, excluding the last (current page),
    // ordered from penultimate to first.
    const candidateUrls = useMemo(() => {
        if (!items || items.length < 2) return []
        return (
            items
                .slice(0, -1)
                .filter((item) => item.navigable !== false && typeof item.url === 'string' && item.url.length > 0)
                .map((item) => item.url as string)
                // Skip the site root (only-locale path, e.g. `/it-it`): it has no url segment to map.
                .filter((url) => toUrlSegment(url).length > 0)
                .reverse()
        )
    }, [items])

    // Stable key so the effect only re-runs when the set of URLs actually changes.
    const candidateKey = candidateUrls.join('|')

    const resolvedUrlsRef = useRef<Set<string>>(new Set())

    useEffect(() => {
        if (candidateUrls.length === 0) return
        const urlsToResolve = candidateUrls.filter((url) => !resolvedUrlsRef.current.has(url))
        if (urlsToResolve.length === 0) return

        let cancelled = false

        const run = async () => {
            const token = await getTokenWhenReady()
            if (cancelled) return
            const headers = {authorization: `Bearer ${token}`}

            // Sequential (concurrency 1) to keep network pressure minimal — intentionally awaiting
            // one request at a time so the background prefetch never floods the network.
            for (const url of urlsToResolve) {
                if (cancelled) return
                resolvedUrlsRef.current.add(url)
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const mapping = await api.shopperSeo.getUrlMapping({
                        parameters: {urlSegment: toUrlSegment(url)},
                        headers,
                    })
                    if (cancelled) return
                    const redirect = resolveRedirect({url: mapping?.c_destinationUrl, status: mapping?.statusCode})
                    if (redirect.shouldRedirect) {
                        setRedirectsByUrl((prev) => ({...prev, [url]: redirect}))
                    }
                } catch {
                    // Ignore: on failure the breadcrumb keeps its original url (lands + bounces
                    // as before). Never block the UX for a background prefetch.
                }
            }
        }

        const cancelIdle = scheduleIdle(() => {
            void run()
        })

        return () => {
            cancelled = true
            cancelIdle()
        }
    }, [candidateKey, api, getTokenWhenReady, candidateUrls])

    return useMemo(
        () => (items ?? []).map((item) => (item.url && redirectsByUrl[item.url] ? {...item, redirect: redirectsByUrl[item.url]} : item)),
        [items, redirectsByUrl],
    )
}
